import { useState } from 'react';
import { useCameras } from '@/hooks/useCameras';
import { useEquipmentAccess } from '@/hooks/useEquipmentAccess';
import { Camera } from '@/data/cameras';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
const sonyLogo = '/logos/sony-logo.svg';
const arriLogo = '/logos/arri-logo.svg';
const blackmagicLogo = '/logos/blackmagic-logo.svg';
const redLogo = '/logos/red-logo.svg';

interface QuickStartCamerasProps {
  onQuickStart: (camera: Camera) => void;
}

// Popular cameras for quick start - these IDs should match database
const QUICK_START_CAMERAS = [
  { id: 'sony-fx3', name: 'FX3', brand: 'Sony', color: 'from-amber-500/20 to-orange-500/20' },
  { id: 'red-komodo-x', name: 'KOMODO-X', brand: 'RED', color: 'from-red-500/20 to-rose-500/20' },
  { id: 'bmpcc-6k-pro', name: 'BMPCC6K Pro', brand: 'Blackmagic', color: 'from-orange-500/20 to-amber-500/20' },
  { id: 'arri-alexa-mini-lf', name: 'ALEXA Mini LF', brand: 'ARRI', color: 'from-blue-600/20 to-sky-500/20' },
];

// Styled SVG text logos for camera brands
// These are above-the-fold on homepage, so we use eager loading with high priority
const BrandLogo = ({ brand }: { brand: string }) => {
  switch (brand) {
    case 'Sony':
      return (
        <img 
          src={sonyLogo} 
          alt="Sony" 
          loading="lazy"
          decoding="async"
          // @ts-expect-error - fetchPriority is valid but not in React types yet
          fetchpriority="high"
          width={40}
          height={20}
          className="w-10 h-5 object-contain brightness-0 dark:invert"
        />
      );
    case 'RED':
      return (
        <img 
          src={redLogo} 
          alt="RED" 
          loading="lazy"
          decoding="async"
          // @ts-expect-error - fetchPriority is valid but not in React types yet
          fetchpriority="high"
          width={40}
          height={20}
          className="w-10 h-5 object-contain"
          style={{ filter: 'invert(16%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(97%) contrast(112%)' }}
        />
      );
    case 'Blackmagic':
      return (
        <img 
          src={blackmagicLogo} 
          alt="Blackmagic Design" 
          loading="lazy"
          decoding="async"
          // @ts-expect-error - fetchPriority is valid but not in React types yet
          fetchpriority="high"
          width={32}
          height={32}
          className="w-8 h-8 object-contain brightness-0 dark:invert"
        />
      );
    case 'ARRI':
      return (
        <img 
          src={arriLogo} 
          alt="ARRI" 
          loading="lazy"
          decoding="async"
          // @ts-expect-error - fetchPriority is valid but not in React types yet
          fetchpriority="high"
          width={40}
          height={20}
          className="w-10 h-5 object-contain"
          style={{ filter: 'invert(24%) sepia(98%) saturate(1500%) hue-rotate(194deg) brightness(92%) contrast(101%)' }}
        />
      );
    default:
      return (
        <span className="text-xs font-display text-muted-foreground">
          {brand.substring(0, 2).toUpperCase()}
        </span>
      );
  }
};

export const QuickStartCameras = ({ onQuickStart }: QuickStartCamerasProps) => {
  const { shouldFilterDemo } = useEquipmentAccess();
  const { cameras, isLoading: camerasLoading, getCameraById } = useCameras({ demoOnly: shouldFilterDemo });
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const handleQuickStart = async (cameraId: string) => {
    setLoadingId(cameraId);
    
    // Find the full camera data
    const camera = getCameraById(cameraId);
    
    if (camera) {
      onQuickStart(camera);
    } else {
      // Camera not found in database - try to find by partial match
      const matchedCamera = cameras.find(c => 
        c.id.toLowerCase().includes(cameraId.toLowerCase()) ||
        cameraId.toLowerCase().includes(c.id.toLowerCase())
      );
      if (matchedCamera) {
        onQuickStart(matchedCamera);
      }
    }
    
    setLoadingId(null);
  };

  // Filter to only show cameras that exist in the database
  const availableCameras = QUICK_START_CAMERAS.filter(qc => {
    const dbCamera = getCameraById(qc.id) || cameras.find(c => 
      c.id.toLowerCase().includes(qc.id.toLowerCase()) ||
      c.name.toLowerCase().includes(qc.name.toLowerCase())
    );
    return !!dbCamera;
  });

  if (camerasLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (availableCameras.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 sm:space-y-4">
      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-display text-foreground">Quick Start</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Jump right in with a popular camera</p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        {availableCameras.map((camera) => (
          <button
            key={camera.id}
            onClick={() => handleQuickStart(camera.id)}
            disabled={loadingId !== null}
            className={cn(
              "group relative p-3 sm:p-4 rounded-lg sm:rounded-xl border border-border/50 bg-card/50",
              "hover:border-primary/50 hover:bg-card transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {/* Gradient background on hover */}
            <div className={cn(
              "absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity",
              camera.color
            )} />
            
            <div className="relative z-10 text-center">
              {loadingId === camera.id ? (
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mx-auto mb-1.5 sm:mb-2 text-primary" />
              ) : (
                <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-1.5 sm:mb-2 rounded-lg bg-secondary/50 flex items-center justify-center">
                  <BrandLogo brand={camera.brand} />
                </div>
              )}
              <p className="font-display text-xs sm:text-sm text-foreground">{camera.name}</p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{camera.brand}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
